import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import CancelOrderModal from '../../../components/CancelOrderModal';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelOrderNumber, setCancelOrderNumber] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');

  useEffect(() => {
    fetchStats();
    // Poll for new orders and updates every 5 seconds
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurant-owner/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (newStatus === 'cancelled') {
      const order = [...(stats?.activeOrders || []), ...(stats?.newOrders || [])].find(o => o._id === orderId);
      setCancelOrderId(orderId);
      setCancelOrderNumber(order?.orderNumber);
      setShowCancelModal(true);
      return;
    }
    setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
    try {
      await axios.patch(`${API_URL}/restaurant-owner/orders/${orderId}/status`, {
        status: newStatus
      });
      await fetchStats();
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!cancelOrderId) return;
    setUpdatingStatus(prev => ({ ...prev, [cancelOrderId]: true }));
    try {
      await axios.patch(`${API_URL}/restaurant-owner/orders/${cancelOrderId}/status`, {
        status: 'cancelled',
        cancellationMessage
      });
      setShowCancelModal(false);
      setCancelOrderId(null);
      setCancelOrderNumber(null);
      await fetchStats();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [cancelOrderId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#f39c12', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      delivered: { bg: '#27ae60', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const filterOrdersBySearch = (orders, query) => {
    if (!query || !query.trim()) return orders || [];
    const q = query.trim().toLowerCase().replace(/\s/g, '');
    return (orders || []).filter((order) => {
      const orderNum = (order.orderNumber || order._id?.slice(-6) || '').toString().toLowerCase();
      const name = (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : order.customerName || '').toLowerCase();
      const email = (order.user?.email || order.customerEmail || '').toLowerCase();
      const phone = (order.customerPhone || order.user?.phoneNumber || '').replace(/\s/g, '').toLowerCase();
      return orderNum.includes(q) || name.includes(q) || email.includes(q) || phone.includes(q);
    });
  };

  const filteredNewOrders = filterOrdersBySearch(stats?.newOrders, orderSearch);
  const filteredActiveOrders = filterOrdersBySearch(stats?.activeOrders, orderSearch);

  const getMaxValue = (data) => {
    if (!data || data.length === 0) return 1;
    return Math.max(...data.map(item => item.sales));
  };

  const formatItemVariations = (item) => {
    if (!item.selectedVariations) return '';
    let variationsText = '';
    if (Array.isArray(item.selectedVariations)) {
      variationsText = item.selectedVariations.map(variation => {
        if (variation.selectedOptions && Array.isArray(variation.selectedOptions)) {
          return `${variation.name}: ${variation.selectedOptions.join(', ')}`;
        }
        return variation.name;
      }).join(' | ');
    } else if (typeof item.selectedVariations === 'object') {
      variationsText = Object.entries(item.selectedVariations).map(([name, options]) => {
        if (Array.isArray(options)) return `${name}: ${options.join(', ')}`;
        return `${name}: ${options}`;
      }).join(' | ');
    }
    return variationsText ? ` (${variationsText})` : '';
  };

  const trimVariationForReceipt = (str) => {
    if (!str) return '';
    return str.replace(/^\s*\(\s*/, '').replace(/\s*\)\s*$/, '');
  };

  const printA4 = (order) => {
    if (!order) return;
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html><html><head><title>Order Receipt - ${order.orderNumber}</title>
      <style>@media print{@page{size:A4;margin:20mm}}body{font-family:Arial,sans-serif;max-width:210mm;margin:0 auto;padding:20px;font-size:14px;color:#000}
      .receipt-header{text-align:center;border-bottom:2px solid #000;padding-bottom:15px;margin-bottom:20px}.receipt-header h1{margin:0 0 10px 0;font-size:24px}
      .receipt-section{margin-bottom:20px}.receipt-section h3{margin:0 0 10px 0;font-size:16px;border-bottom:1px solid #ccc;padding-bottom:5px}
      .receipt-row{display:flex;justify-content:space-between;margin:5px 0}.receipt-row-label{font-weight:600;width:150px}
      .receipt-items{margin:10px 0}.receipt-item{margin:8px 0;padding:8px;background:#f5f5f5;border-radius:4px}
      .receipt-item-name{font-weight:600;margin-bottom:4px}.receipt-item-variations{font-size:12px;color:#666;margin:4px 0}
      .receipt-item-details{display:flex;justify-content:space-between;margin-top:4px}.receipt-total{border-top:2px solid #000;margin-top:20px;padding-top:15px;font-weight:bold}
      .receipt-total-row{display:flex;justify-content:space-between;margin:8px 0;font-size:16px}.receipt-total-final{font-size:20px;border-top:1px solid #000;padding-top:10px;margin-top:10px}
      .receipt-footer{margin-top:30px;padding-top:15px;border-top:1px solid #ccc;text-align:center;font-size:12px;color:#666}</style></head><body>
      <div class="receipt-header"><h1>${order.restaurantName || (order.restaurant?.name) || 'Order Receipt'}</h1>
      <p><strong>Order Number:</strong> ${order.orderNumber}</p><p><strong>Date:</strong> ${new Date(order.createdAt).toLocaleString('en-GB')}</p></div>
      <div class="receipt-section"><h3>Customer</h3>
      <div class="receipt-row"><span class="receipt-row-label">Name:</span><span>${order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || 'N/A')}</span></div>
      <div class="receipt-row"><span class="receipt-row-label">Email:</span><span>${order.user?.email ?? order.customerEmail ?? 'N/A'}</span></div>
      <div class="receipt-row"><span class="receipt-row-label">Phone:</span><span>${order.customerPhone ?? order.user?.phoneNumber ?? 'N/A'}</span></div></div>
      ${order.deliveryAddress ? `<div class="receipt-section"><h3>Delivery Address</h3>
      <div class="receipt-row"><span class="receipt-row-label">Address:</span><span>${order.deliveryAddress.street || ''}</span></div>
      <div class="receipt-row"><span class="receipt-row-label">City:</span><span>${order.deliveryAddress.city || ''}, ${order.deliveryAddress.zipCode || ''}</span></div></div>` : ''}
      <div class="receipt-section"><h3>Order Items</h3><div class="receipt-items">
      ${(order.items || []).map(item => `<div class="receipt-item"><div class="receipt-item-name">${item.name} x${item.quantity}</div>
      ${formatItemVariations(item) ? `<div class="receipt-item-variations">${trimVariationForReceipt(formatItemVariations(item))}</div>` : ''}
      <div class="receipt-item-details"><span>€${item.price?.toFixed(2)}</span><span><strong>€${(item.price * item.quantity)?.toFixed(2)}</strong></span></div></div>`).join('') || '<p>No items</p>'}
      </div></div>
      <div class="receipt-section"><h3>Pricing</h3><div class="receipt-total">
      <div class="receipt-total-row"><span>Subtotal:</span><span>€${(order.subtotalBeforeTax ?? order.subtotal)?.toFixed(2) || '0.00'}</span></div>
      ${(order.taxAmount || 0) > 0 ? `<div class="receipt-total-row"><span>Tax:</span><span>€${order.taxAmount?.toFixed(2)}</span></div>` : ''}
      <div class="receipt-total-row"><span>Delivery:</span><span>€${order.deliveryFee?.toFixed(2) || '0.00'}</span></div>
      ${(order.serviceFee || 0) > 0 ? `<div class="receipt-total-row"><span>Service:</span><span>€${order.serviceFee?.toFixed(2)}</span></div>` : ''}
      <div class="receipt-total-row receipt-total-final"><span><strong>TOTAL:</strong></span><span><strong>€${order.total?.toFixed(2)}</strong></span></div></div></div>
      <div class="receipt-footer"><p>Thank you for your order!</p><p>${new Date().toLocaleString('en-GB')}</p></div></body></html>`;
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const printReceipt = (order) => {
    if (!order) return;
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html><html><head><title>Receipt - ${order.orderNumber}</title>
      <style>@media print{@page{size:80mm auto;margin:0}body{margin:0;padding:10px;font-size:11px}}body{font-family:'Courier New',monospace;width:80mm;margin:0 auto;padding:10px;font-size:11px}
      .receipt-header{text-align:center;border-bottom:1px dashed #000;padding-bottom:8px;margin-bottom:8px}.receipt-header h2{margin:0 0 5px 0;font-size:14px}
      .receipt-section{margin:8px 0;padding:5px 0;border-bottom:1px dashed #ccc}.receipt-section-title{font-weight:bold;margin-bottom:3px;font-size:10px}
      .receipt-row{font-size:10px;margin:2px 0}.receipt-item{display:flex;justify-content:space-between;margin:4px 0;padding:3px 0}
      .receipt-item-name{font-weight:bold;margin-bottom:2px}.receipt-item-variations{font-size:9px;color:#555;margin:2px 0}
      .receipt-total{border-top:1px dashed #000;margin-top:10px;padding-top:8px;font-weight:bold}.receipt-total-row{display:flex;justify-content:space-between;margin:3px 0}
      .receipt-footer{margin-top:10px;text-align:center;font-size:9px;border-top:1px dashed #000;padding-top:8px}</style></head><body>
      <div class="receipt-header"><h2>${order.restaurantName || (order.restaurant?.name) || 'Order'}</h2><p>Order #: ${order.orderNumber}</p>
      <p>${new Date(order.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
      <div class="receipt-section"><div class="receipt-section-title">CUSTOMER</div>
      <div class="receipt-row">${order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || 'N/A')}</div>
      <div class="receipt-row">${order.user?.email ?? order.customerEmail ?? 'N/A'}</div>${(order.customerPhone ?? order.user?.phoneNumber) ? `<div class="receipt-row">${order.customerPhone ?? order.user?.phoneNumber}</div>` : ''}</div>
      ${order.deliveryAddress ? `<div class="receipt-section"><div class="receipt-section-title">DELIVERY ADDRESS</div>
      <div class="receipt-row">${order.deliveryAddress.street || ''}</div>
      ${order.deliveryAddress.addressDetails ? `<div class="receipt-row">${order.deliveryAddress.addressDetails}</div>` : ''}
      <div class="receipt-row">${order.deliveryAddress.city || ''}, ${order.deliveryAddress.zipCode || ''}</div></div>` : ''}
      <div class="receipt-section"><div class="receipt-section-title">ITEMS</div>
      ${(order.items || []).map(item => `<div class="receipt-item"><div style="flex:1"><div class="receipt-item-name">${item.name} x${item.quantity}</div>
      ${formatItemVariations(item) ? `<div class="receipt-item-variations">${trimVariationForReceipt(formatItemVariations(item))}</div>` : ''}</div>
      <div>€${(item.price * item.quantity)?.toFixed(2)}</div></div>`).join('')}
      </div><div class="receipt-section"><div class="receipt-total">
      <div class="receipt-total-row"><span>TOTAL:</span><span>€${order.total?.toFixed(2)}</span></div></div></div>
      <div class="receipt-footer">${new Date().toLocaleString('en-GB')}</div></body></html>`;
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  if (loading) {
    return <div className="loading-spinner">{t('admin.loadingDashboard')}</div>;
  }

  return (
    <div className="owner-dashboard">
      <h2>{t('admin.dashboardOverview')}</h2>

      <div className="dashboard-orders-search">
        <label htmlFor="order-search" className="dashboard-search-label">
          <i className="fas fa-search"></i>
          <span>{t('admin.searchOrders') || 'Search orders'}</span>
        </label>
        <input
          id="order-search"
          type="text"
          className="dashboard-search-input"
          placeholder={t('admin.searchOrdersPlaceholder') || 'Order number, customer name, email, phone...'}
          value={orderSearch}
          onChange={(e) => setOrderSearch(e.target.value)}
        />
      </div>

      {/* New Orders (Accepting orders) – first */}
      <div className="new-orders-section">
        <h3>{t('admin.newOrders')}</h3>
        {filteredNewOrders.length > 0 ? (
          <div className="orders-list">
            {filteredNewOrders.map(order => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                    <p className="order-customer">
                      <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                    </p>
                    <p className="order-time">
                      <i className="fas fa-clock"></i> {formatDate(order.createdAt)}
                    </p>
                    {(order.customerPhone || order.user?.phoneNumber) && (
                      <p className="order-phone">
                        <i className="fas fa-phone"></i> {order.customerPhone ?? order.user?.phoneNumber}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(order.status)}
                </div>
                
                <div className="order-details">
                  <div className="order-items">
                    <strong>{t('admin.items')}:</strong>
                    <ul>
                      {order.items?.map((item, idx) => (
                        <li key={idx} className="order-item-with-variations">
                          <div className="order-item-main">
                            <span className="item-quantity">{item.quantity}x</span>
                            <span className="item-name">{item.name}</span>
                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.selectedVariations && (
                            <div className="order-item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variation.name}:</span>
                                    <div className="order-variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="order-variation-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variationName}:</span>
                                    <div className="order-variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="order-variation-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="order-variation-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="order-total">
                    <strong>{t('admin.total')}: {formatCurrency(order.total)}</strong>
                  </div>
                  {order.deliveryAddress && (
                    <div className="order-address">
                      <strong>{t('admin.deliveryAddress')}:</strong> {order.deliveryAddress.street}, {order.deliveryAddress.city}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}
                    </div>
                  )}
                  {(order.restaurantInstructions || order.driverInstructions || order.customerInstructions) && (
                    <div className="order-instructions">
                      <strong>{t('admin.customerInstructionsSection')}:</strong>{' '}
                      {[order.restaurantInstructions, order.driverInstructions].filter(Boolean).join(' · ') || order.customerInstructions}
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  {order.status === 'pending' && (
                    <>
                      <button
                        className="btn-accept"
                        onClick={() => handleStatusChange(order._id, 'confirmed')}
                        disabled={updatingStatus[order._id]}
                      >
                        {updatingStatus[order._id] ? t('admin.updating') : t('admin.accept')}
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={() => handleStatusChange(order._id, 'cancelled')}
                        disabled={updatingStatus[order._id]}
                      >
                        {t('admin.cancel')}
                      </button>
                    </>
                  )}
                  {order.status === 'confirmed' && (
                    <button
                      className="btn-preparing"
                      onClick={() => handleStatusChange(order._id, 'preparing')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.startPreparing')}
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      className="btn-out-for-delivery"
                      onClick={() => handleStatusChange(order._id, 'outForDelivery')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.outForDelivery')}
                    </button>
                  )}
                  {order.status !== 'cancelled' && order.status !== 'pending' && (
                    <button
                      className="btn-cancel"
                      onClick={() => handleStatusChange(order._id, 'cancelled')}
                      disabled={updatingStatus[order._id]}
                    >
                      {t('admin.cancelOrder')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>{t('admin.noNewOrders')}</p>
          </div>
        )}
      </div>

      {/* Active Orders Section – second */}
      <div className="active-orders-section">
        <h3>{t('admin.activeOrders')}</h3>
        {filteredActiveOrders.length > 0 ? (
          <div className="orders-list">
            {filteredActiveOrders.map(order => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div>
                    <h4>{t('admin.orderNumber')} {order.orderNumber || order._id.slice(-6)}</h4>
                    <p className="order-customer">
                      <i className="fas fa-user"></i> {order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : (order.customerName || '—')}
                    </p>
                    <p className="order-time">
                      <i className="fas fa-clock"></i> {formatDate(order.updatedAt || order.createdAt)}
                    </p>
                    {(order.customerPhone || order.user?.phoneNumber) && (
                      <p className="order-phone">
                        <i className="fas fa-phone"></i> {order.customerPhone ?? order.user?.phoneNumber}
                      </p>
                    )}
                    {order.deliveryDriver && (
                      <p className="order-driver">
                        <i className="fas fa-truck"></i> {t('admin.driver')}: {order.deliveryDriver.firstName} {order.deliveryDriver.lastName}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(order.status)}
                </div>
                
                <div className="order-details">
                  <div className="order-items">
                    <strong>{t('admin.items')}:</strong>
                    <ul>
                      {order.items?.map((item, idx) => (
                        <li key={idx} className="order-item-with-variations">
                          <div className="order-item-main">
                            <span className="item-quantity">{item.quantity}x</span>
                            <span className="item-name">{item.name}</span>
                            <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.selectedVariations && (
                            <div className="order-item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variation.name}:</span>
                                    <div className="order-variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="order-variation-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="order-variation-group">
                                    <span className="order-variation-name">{variationName}:</span>
                                    <div className="order-variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="order-variation-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="order-variation-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="order-total">
                    <strong>{t('admin.total')}: {formatCurrency(order.total)}</strong>
                  </div>
                  {order.deliveryAddress && (
                    <div className="order-address">
                      <strong>{t('admin.deliveryAddress')}:</strong> {order.deliveryAddress.street}, {order.deliveryAddress.city}{order.deliveryAddress.addressDetails ? ` — ${order.deliveryAddress.addressDetails}` : ''}
                    </div>
                  )}
                  {(order.restaurantInstructions || order.driverInstructions || order.customerInstructions) && (
                    <div className="order-instructions">
                      <strong>{t('admin.customerInstructionsSection')}:</strong>{' '}
                      {[order.restaurantInstructions, order.driverInstructions].filter(Boolean).join(' · ') || order.customerInstructions}
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  <Link
                    to="/admin/restaurant/messages"
                    state={{ orderId: order._id }}
                    className="btn-chat"
                  >
                    <i className="fas fa-comments"></i> {t('admin.orderChat')}
                  </Link>
                  <button type="button" className="btn-print-a4" onClick={() => printA4(order)}>
                    <i className="fas fa-print"></i> {t('admin.printA4')}
                  </button>
                  <button type="button" className="btn-print-receipt" onClick={() => printReceipt(order)}>
                    <i className="fas fa-receipt"></i> {t('admin.printReceipt')}
                  </button>
                  {order.status === 'confirmed' && (
                    <button
                      className="btn-preparing"
                      onClick={() => handleStatusChange(order._id, 'preparing')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.startPreparing')}
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button
                      className="btn-out-for-delivery"
                      onClick={() => handleStatusChange(order._id, 'outForDelivery')}
                      disabled={updatingStatus[order._id]}
                    >
                      {updatingStatus[order._id] ? t('admin.updating') : t('admin.outForDelivery')}
                    </button>
                  )}
                  {order.status !== 'cancelled' && (
                    <button
                      className="btn-cancel"
                      onClick={() => handleStatusChange(order._id, 'cancelled')}
                      disabled={updatingStatus[order._id]}
                    >
                      {t('admin.cancelOrder')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-orders">
            <p>{t('admin.noActiveOrders')}</p>
          </div>
        )}
      </div>

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={cancelOrderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => { setShowCancelModal(false); setCancelOrderId(null); setCancelOrderNumber(null); }}
      />

      {/* Today's orders & Today's sales */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{t('admin.todaysOrders')}</h3>
          <p className="stat-number">{stats?.todaysOrdersCount || 0}</p>
        </div>
        <div className="stat-card">
          <h3>{t('admin.todaysSales')}</h3>
          <p className="stat-number">{formatCurrency(stats?.todaysSales || 0)}</p>
        </div>
      </div>

      {/* This month's sales */}
      {stats?.monthlySalesChart && stats.monthlySalesChart.length > 0 && (
        <div className="chart-container">
          <h3>{t('admin.thisMonthsSales')}</h3>
          <div className="chart-bars">
            {stats.monthlySalesChart.map((day, index) => {
              const maxSales = getMaxValue(stats.monthlySalesChart);
              return (
                <div key={index} className="chart-bar-container">
                  <div 
                    className="chart-bar monthly" 
                    style={{ height: `${(day.sales / maxSales) * 200}px` }}
                  >
                    <span className="chart-value">{formatCurrency(day.sales)}</span>
                  </div>
                  <span className="chart-label">{day.day}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
